import { useLeaderboard, LeaderboardEntry } from '../hooks/useLeaderboard';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { Trophy, Star, TrendingUp, Award } from 'lucide-react';
import { motion } from 'framer-motion';

const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="w-10 h-10 text-yellow-400 drop-shadow-lg" />;
    if (rank === 2) return <Trophy className="w-9 h-9 text-gray-400 drop-shadow-md" />;
    if (rank === 3) return <Trophy className="w-8 h-8 text-orange-400 drop-shadow" />;
    return <span className="text-2xl font-bold text-gray-600">{rank}</span>;
};

const LeaderboardRow = ({ entry, currentUser, sortBy }: { entry: LeaderboardEntry; currentUser: boolean, sortBy: string }) => (
    <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className={`flex items-center p-4 my-3 rounded-full transition-all duration-300 ${currentUser ? 'bg-purple-200 scale-105 shadow-2xl border-4 border-purple-400' : 'bg-white shadow-lg'}`}>
        <div className="w-16 text-center flex-shrink-0">{getRankIcon(entry.rank)}</div>
        <div className="flex items-center flex-grow ml-2">
            <img
                src={entry.avatar_url || `https://api.dicebear.com/7.x/adventurer/svg?seed=${entry.id}`}
                alt={entry.display_name}
                className="w-14 h-14 rounded-full border-4 border-white shadow-md"
            />
            <div className="ml-4">
                <p className={`font-bold text-xl ${currentUser ? 'text-purple-900' : 'text-gray-800'}`}>
                    {entry.display_name}
                </p>
                <p className="text-sm text-gray-600 font-medium">Level {entry.level}</p>
            </div>
        </div>
        <div className="hidden md:flex items-center space-x-4 text-right">
            <div className={`text-center p-3 rounded-lg transition-all duration-200 ${sortBy === 'total_score' ? 'bg-yellow-100 ring-2 ring-yellow-300' : ''}`}>
                <p className="text-2xl font-bold text-yellow-600">{entry.total_score}</p>
                <p className="text-xs text-gray-500">Points</p>
            </div>
            <div className={`text-center p-3 rounded-lg transition-all duration-200 ${sortBy === 'streak_days' ? 'bg-red-100 ring-2 ring-red-300' : ''}`}>
                <p className="text-2xl font-bold text-red-500">{entry.streak_days}</p>
                <p className="text-xs text-gray-500">Streak</p>
            </div>
            <div className={`text-center p-3 rounded-lg transition-all duration-200 ${sortBy === 'level' ? 'bg-green-100 ring-2 ring-green-300' : ''}`}>
                <p className="text-2xl font-bold text-green-600">{entry.words_mastered}</p>
                <p className="text-xs text-gray-500">Words</p>
            </div>
        </div>
    </motion.div>
);

export default function LeaderboardPage() {
    const {
        leaderboard,
        userRank,
        isLoading,
        error,
        timeframe,
        setTimeframe,
        sortBy,
        setSortBy,
        profile,
    } = useLeaderboard();

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-blue-50">
                <LoadingSpinner size="lg" />
            </div>
        );
    }

    if (error) {
        return <div className="text-center py-10 text-red-500">Error: {error.message}</div>;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-blue-50 to-purple-100 font-sans p-4 sm:p-6 lg:p-8">
            <div className="max-w-6xl mx-auto">
                <header className="text-center mb-10">
                    <motion.h1
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                        className="text-6xl font-bold text-purple-700 tracking-tight drop-shadow-md"
                    >
                        Hall of Fame
                    </motion.h1>
                    <p className="text-gray-600 mt-3 text-xl">See who's a learning superstar!</p>
                </header>

                <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8 bg-white/80 backdrop-blur-lg p-4 rounded-full shadow-lg ring-1 ring-gray-200">
                    <div className="flex gap-2">
                        {(['all-time', 'monthly', 'weekly'] as const).map((tf) => (
                            <button
                                key={tf}
                                onClick={() => setTimeframe(tf)}
                                className={`px-5 py-2 rounded-full font-semibold transition-all duration-300 text-base ${timeframe === tf ? 'bg-purple-600 text-white shadow-lg' : 'bg-white text-gray-700 hover:bg-gray-100'}`}>
                                {tf.charAt(0).toUpperCase() + tf.slice(1)}
                            </button>
                        ))}
                    </div>
                    <div className="w-px h-8 bg-gray-300 hidden sm:block"></div>
                    <div className="flex gap-2">
                        {[
                            { id: 'total_score', label: 'Points', icon: <Star className="w-5 h-5 mr-1.5" /> },
                            { id: 'level', label: 'Level', icon: <Award className="w-5 h-5 mr-1.5" /> },
                            { id: 'streak_days', label: 'Streak', icon: <TrendingUp className="w-5 h-5 mr-1.5" /> },
                        ].map((sb) => (
                            <button
                                key={sb.id}
                                onClick={() => setSortBy(sb.id as any)}
                                className={`flex items-center px-5 py-2 rounded-full font-semibold transition-all duration-300 text-base ${sortBy === sb.id ? 'bg-purple-600 text-white shadow-lg' : 'bg-white text-gray-700 hover:bg-gray-100'}`}>
                                {sb.icon} {sb.label}
                            </button>
                        ))}
                    </div>
                </motion.div>

                {userRank && profile && (
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                        className="mb-10 p-4 bg-purple-50 rounded-2xl shadow-inner">
                        <h2 className="text-2xl font-bold text-center text-purple-800 mb-3">Your Spot in the Ranks</h2>
                        <LeaderboardRow entry={userRank} currentUser={true} sortBy={sortBy} />
                    </motion.div>
                )}

                <main className="bg-white/50 backdrop-blur-md rounded-2xl p-4 shadow-xl ring-1 ring-gray-200">
                    {leaderboard.map((entry) => (
                        <LeaderboardRow key={entry.id} entry={entry} currentUser={profile?.id === entry.id} sortBy={sortBy} />
                    ))}
                </main>
            </div>
        </div>
    );
}